import { HttpClient, HttpEventType } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ClientAnalyzePayload } from './client-audio-analyze.service';
import { ReviewCaptionSegment } from '../../Review/models/review-video.model';
import { ReviewClientRenderRuntime } from '../../Review/models/review-browser-render.model';
import { buildPipelineAssetProxyUrl, storageKeyFromRenderableUrl } from '../../Review/services/review-media-proxy-url';

// A non-empty-blob floor: an encoded render is always many KB; a sub-1KB blob means the
// encode produced nothing. Guard before the PUT so a blank output is never uploaded.
const MIN_RENDER_OUTPUT_BYTES = 512;

export interface ProcessingSignedUploadTarget {
  assetId: string;
  url: string;
  filePath: string;
  requestedFilePath: string;
  verb: 'PUT' | 'GET' | string;
  contentType?: string | null;
  expiresAtUtc: string;
  expiresInMinutes: number;
}

export interface ProcessingRawUploadReservation {
  jobId: string;
  jobIdShort: string;
  targetId: string;
  rawFolderPath: string;
  sourceUpload: ProcessingSignedUploadTarget;
  metadataUpload: ProcessingSignedUploadTarget;
}

export interface ProcessingRenderMetadata {
  hook?: string | null;
  script?: string | null;
  sourceFilePath?: string | null;
  rawMetadataFilePath?: string | null;
  tempAudioKey?: string | null;
}

export interface ProcessingRenderComposition {
  kind?: string | null;
  startTime: number;
  endTime: number;
  durationSeconds: number;
  captions?: ReviewCaptionSegment[] | null;
  hook?: string | null;
  script?: string | null;
}

export interface ProcessingClientRenderShort {
  title?: string | null;
  description?: string | null;
  startTime: number;
  endTime: number;
  durationSeconds: number;
  segments?: ReviewCaptionSegment[] | null;
  hook?: string | null;
  script?: string | null;
  composition?: ProcessingRenderComposition | null;
}

export interface ProcessingClientRenderPayload {
  jobId?: string | null;
  title?: string | null;
  description?: string | null;
  transcript?: string | null;
  segments?: ReviewCaptionSegment[] | null;
  shorts?: ProcessingClientRenderShort[] | null;
  durationSeconds?: number | null;
  runtime?: string | null;
  metadata?: ProcessingRenderMetadata | null;
  mainComposition?: ProcessingRenderComposition | null;
}

export interface SubmitProcessingJobResult {
  jobId?: string | null;
  message?: string | null;
  mode?: 'client-render' | 'queued' | string | null;
  clientRender?: ProcessingClientRenderPayload | null;
  JobId?: string | null;
  Message?: string | null;
  Mode?: string | null;
  ClientRender?: ProcessingClientRenderPayload | null;
}

export interface ProcessingJobStatus {
  id?: string;
  jobId?: string;
  title?: string;
  status?: string | number;
  progress?: number;
  errorMessage?: string | null;
}

export interface ProcessingJobDetailedStatus {
  jobId: string;
  title?: string | null;
  status?: string | null;
  currentStep?: string | null;
  lastCompletedStep?: string | null;
  progress?: number | null;
  errorMessage?: string | null;
  checkpointResourcesJson?: string | null;
  metadata?: string | null;
}

export interface ProcessingReviewTargetMetadata {
  hook?: string | null;
  script?: string | null;
  transcript?: string | null;
  runtime?: ReviewClientRenderRuntime | string | null;
  segmentCount?: number | null;
}

export interface ProcessingReviewTargetReservation {
  jobId: string;
  reviewItemId: string;
  folderId: string;
  videoAssetId: string;
  thumbnailAssetId?: string | null;
  videoUpload: ProcessingSignedUploadTarget;
  thumbnailUpload?: ProcessingSignedUploadTarget | null;
  thumbnailUploads?: ProcessingSignedUploadTarget[] | null;
}

@Injectable({ providedIn: 'root' })
export class ProcessingJobApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/processing`;

  getUserProcessingOptions(): Observable<any> {
    return this.http.get<any>(`${this.base}/options`);
  }

  reserveRawUploadTarget(req: {
    fileName: string;
    contentType?: string | null;
    fileSizeBytes?: number | null;
  }): Observable<ProcessingRawUploadReservation> {
    return this.http.post<ProcessingRawUploadReservation>(`${this.base}/uploads/raw/reserve`, req);
  }

  submitProcessingJob(req: {
    reservedJobId: string;
    fileName: string;
    filePath: string;
    rawMetadataFilePath: string;
    options: any;
    clientAnalyze: ClientAnalyzePayload;
  }): Observable<SubmitProcessingJobResult> {
    return this.http.post<SubmitProcessingJobResult>(`${this.base}/jobs/submit`, req);
  }

  updateRuntimeJob(
    jobId: string,
    data: {
      status: string;
      progress: number;
      currentStep?: string | null;
      errorMessage?: string | null;
      stepOutputJson?: unknown;
    }
  ): Observable<void> {
    return this.http.post<void>(`${this.base}/jobs/${jobId}/runtime-status`, data);
  }

  getProcessingJobStatus(jobId: string): Observable<ProcessingJobDetailedStatus> {
    return this.http.get<ProcessingJobDetailedStatus>(`${this.base}/jobs/${jobId}/status`);
  }

  getProcessingJobs(): Observable<ProcessingJobStatus[]> {
    return this.http.get<ProcessingJobStatus[]>(`${this.base}/jobs`);
  }

  reserveReviewTarget(req: {
    jobId: string;
    title: string;
    description?: string | null;
    isShort: boolean;
    videoContentType?: string | null;
    thumbnailContentType?: string | null;
    thumbnailCount?: number | null;
    durationSeconds?: number | null;
    metadata?: ProcessingReviewTargetMetadata | null;
  }): Observable<ProcessingReviewTargetReservation> {
    return this.http.post<ProcessingReviewTargetReservation>(
      `${this.base}/jobs/${req.jobId}/review-targets/reserve`,
      {
        title: req.title,
        description: req.description ?? null,
        isShort: req.isShort,
        videoContentType: req.videoContentType ?? null,
        thumbnailContentType: req.thumbnailContentType ?? null,
        thumbnailCount: req.thumbnailCount ?? null,
        durationSeconds: req.durationSeconds ?? null,
        metadata: req.metadata ?? null
      }
    );
  }

  async uploadBlobToTarget(
    blob: Blob,
    target: ProcessingSignedUploadTarget,
    contentType?: string | null,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    // 0-byte guard: reject a blank/empty render BEFORE the PUT so we never persist nothing.
    if (!blob || blob.size < MIN_RENDER_OUTPUT_BYTES) {
      throw new Error(`Render produced a blank/empty output (${blob?.size ?? 0} bytes). Nothing was uploaded.`);
    }

    const resolvedContentType = contentType || target.contentType || blob.type || 'application/octet-stream';

    // Direct-to-R2 first (presigned PUT) so render bytes never transit the API server.
    // Fall back to the API proxy ONLY if the direct upload fails (e.g. R2 CORS not yet
    // configured for this origin). Mirrors ReviewIntakeApiService.putBrowserFileToTarget so
    // a CORS/network failure retries via the proxy instead of losing the rendered file.
    try {
      await this.putWithProgress(target.url, blob, resolvedContentType, onProgress);
    } catch {
      const storageKey = storageKeyFromRenderableUrl(target.filePath);
      if (!storageKey) {
        throw new Error('Render upload failed and no proxy fallback is available.');
      }
      const proxyUrl = buildPipelineAssetProxyUrl(environment.apiUrl, storageKey, resolvedContentType);
      // Reset progress for the proxy retry so the percentage tracks the second attempt.
      await this.putWithProgress(proxyUrl, blob, resolvedContentType, onProgress);
    }
  }

  // PUT a blob and (when onProgress is supplied) report 0–100 upload progress from
  // HttpEventType.UploadProgress. With no callback this resolves on completion exactly
  // like the prior HttpClient.put — existing callers are unaffected.
  private async putWithProgress(
    url: string,
    blob: Blob,
    contentType: string,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    if (!onProgress) {
      await firstValueFrom(this.http.put(url, blob, {
        headers: { 'Content-Type': contentType }
      }));
      return;
    }

    await firstValueFrom(
      this.http.put(url, blob, {
        headers: { 'Content-Type': contentType },
        reportProgress: true,
        observe: 'events'
      }).pipe(
        tap(event => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            onProgress(Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100))));
          }
        })
      )
    );
  }

  async uploadDesktopFileToTarget(
    filePath: string,
    target: ProcessingSignedUploadTarget,
    contentType?: string | null
  ): Promise<void> {
    const bridge = window.heyjivuDesktop?.review;
    if (!bridge?.uploadFile) {
      throw new Error('Desktop upload bridge is unavailable.');
    }

    const resolvedContentType = contentType || target.contentType || 'application/octet-stream';

    // Direct-to-R2 first (presigned PUT via the desktop bridge). Fall back to the API proxy
    // ONLY if the direct upload fails (e.g. R2 CORS not yet configured) by pointing the bridge
    // at the proxy URL instead of the presigned URL — mirrors the browser proxy fallback so a
    // CORS/network failure retries via the proxy instead of losing the rendered file.
    const result = await bridge.uploadFile({
      url: target.url,
      filePath,
      contentType: resolvedContentType
    });

    if (!result.ok) {
      const storageKey = storageKeyFromRenderableUrl(target.filePath);
      if (!storageKey) {
        throw new Error(`Desktop upload failed with HTTP ${result.status}.`);
      }
      const proxyUrl = buildPipelineAssetProxyUrl(environment.apiUrl, storageKey, resolvedContentType);
      const proxied = await bridge.uploadFile({
        url: proxyUrl,
        filePath,
        contentType: resolvedContentType
      });
      if (!proxied.ok) {
        throw new Error(`Desktop upload failed with HTTP ${proxied.status}.`);
      }
    }
  }

  async uploadMobileNativeFileToTarget(
    filePath: string,
    target: ProcessingSignedUploadTarget,
    contentType?: string | null
  ): Promise<void> {
    const bridge = window.Capacitor?.Plugins?.JivuRender;
    if (!bridge?.uploadFile) {
      throw new Error('Android native upload bridge is unavailable.');
    }

    const result = await bridge.uploadFile({
      url: target.url,
      filePath,
      contentType: contentType || target.contentType || 'application/octet-stream'
    });

    if (!result.ok) {
      throw new Error(`Android native upload failed with HTTP ${result.status}.`);
    }
  }
}
